
import React, { ReactNode, useEffect } from 'react';
import { XMarkIcon } from '../icons/XMarkIcon'; // Assuming you'll create this

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'fit' | 'full'; // Added 'full'
}

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children, size = 'md' }) => {
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-sm max-h-[90vh]',
    md: 'max-w-md max-h-[90vh]',
    lg: 'max-w-lg max-h-[90vh]',
    xl: 'max-w-xl max-h-[90vh]',
    '2xl': 'max-w-2xl max-h-[90vh]',
    fit: 'max-w-fit max-h-[90vh]',
    full: 'w-[95vw] h-[95vh]',
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 transition-opacity duration-300 ease-in-out"
      onClick={onClose} // Close on backdrop click
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? "modal-title" : undefined}
    >
      <div 
        className={`bg-white dark:bg-dark-secondary rounded-lg shadow-xl transform transition-all duration-300 ease-in-out w-full flex flex-col ${sizeClasses[size]}`}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside modal content
      >
        <div className="flex-shrink-0 flex justify-between items-center p-4 border-b border-gray-200 dark:border-dark-tertiary">
          {title && <h3 id="modal-title" className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary truncate">{title}</h3>}
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-dark-text-secondary hover:text-gray-900 dark:hover:text-dark-text-primary p-1 rounded-full hover:bg-gray-100 dark:hover:bg-dark-tertiary transition-colors"
            aria-label="Fechar modal"
          >
            <XMarkIcon className="text-2xl" />
          </button>
        </div>
        <div className={`flex-grow overflow-auto ${size === 'full' ? 'p-0' : 'p-4 md:p-6'}`}>
          {children}
        </div>
      </div>
    </div>
  );
};
